/**
 * dopamiles-search.js
 * Predictive search dropdown for BNC theme.
 *
 * Responsibilities:
 *  - Open/close panel when header search input is focused/blurred
 *  - Show idle state (recent searches + suggested queries + preview cards)
 *  - Fetch /search/suggest.json on input, render result rows
 *  - Persist recent searches in localStorage (max 5)
 *  - Keyboard navigation (↑ ↓ Enter Esc) within the panel
 *
 * Depends on: dopamiles-search.css
 * Panel anchor: any element with [data-bnc-search-trigger] wrapping an <input>
 */

(function () {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────

  const SUGGEST_URL    = '/search/suggest.json';
  const SUGGEST_LIMIT  = 8;
  const RECENT_KEY     = 'dop_recent_searches';
  const RECENT_MAX     = 5;
  const DEBOUNCE_MS    = 220;

  // ── State ──────────────────────────────────────────────────────────────────

  let currentQuery     = '';
  let debounceTimer    = null;
  let activeIndex      = -1; // keyboard nav index
  let panel            = null;
  let inputEl          = null;
  let triggerWrap      = null;

  // ── localStorage helpers ──────────────────────────────────────────────────

  function getRecent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (_) {
      return [];
    }
  }

  function saveRecent(query) {
    const q = query.trim();
    if (!q) return;
    let list = getRecent().filter((s) => s.toLowerCase() !== q.toLowerCase());
    list.unshift(q);
    if (list.length > RECENT_MAX) list = list.slice(0, RECENT_MAX);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (_) {}
  }

  function clearRecent() {
    try { localStorage.removeItem(RECENT_KEY); } catch (_) {}
  }

  // ── Money formatter ───────────────────────────────────────────────────────

  function formatMoney(price) {
    // /search/suggest.json returns `price` as a string in store currency
    // (e.g. "25.00"), already in dollars. Earlier `cents / 100` assumed a
    // cents integer and produced bug $0.25 from a $25.00 product.
    if (price === null || price === undefined || price === '') return '';
    const dollars = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (isNaN(dollars)) return '';
    return '$' + dollars.toFixed(2).replace(/\.00$/, '');
  }

  // ── Panel HTML builders ───────────────────────────────────────────────────

  /** Build the idle state: recent + suggested + preview cards.
   *  Each section is only emitted when it has real content — empty placeholders
   *  ("No recent searches yet.", "Browse our catalog.") were noisy. Featured
   *  always renders (skeletons until fetchSuggestions resolves). */
  function buildIdleHTML() {
    const recent    = getRecent();
    const suggested = getSuggestedQueries();
    const cols = [];

    if (recent.length) {
      cols.push(`
        <div class="bnc-search-col">
          <div class="bnc-search-col-head">
            Recent
            <button type="button" class="bnc-clear-recent" id="bnc-clear-recent">Clear</button>
          </div>
          <ul class="bnc-search-list" id="bnc-recent-list">
            ${recent.map((q) => `
              <li>
                <a href="/search?q=${encodeURIComponent(q)}&type=product">
                  <span class="bnc-sl-lhs">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                    </svg>
                    ${escHtml(q)}
                  </span>
                </a>
              </li>`).join('')}
          </ul>
        </div>`);
    }

    if (suggested.length) {
      cols.push(`
        <div class="bnc-search-col">
          <div class="bnc-search-col-head">Suggestions</div>
          <ul class="bnc-search-list">
            ${suggested.map((q) => `
              <li>
                <a href="/search?q=${encodeURIComponent(q)}&type=product">
                  <span class="bnc-sl-lhs">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                    </svg>
                    ${escHtml(q)}
                  </span>
                </a>
              </li>`).join('')}
          </ul>
        </div>`);
    }

    cols.push(`
      <div class="bnc-search-col">
        <div class="bnc-search-col-head">Featured</div>
        <div class="bnc-search-preview-grid" id="bnc-preview-grid">
          ${buildSkeletonCards(4)}
        </div>
      </div>`);

    return `
      <div class="bnc-search-idle" data-cols="${cols.length}">
        ${cols.join('')}
      </div>
      ${buildFooterHTML()}`;
  }

  function buildSkeletonCards(n) {
    return Array.from({ length: n }, () => `
      <div class="bnc-search-pcard" aria-hidden="true">
        <div class="bnc-search-pcard-img bnc-skel" style="aspect-ratio:1"></div>
        <div class="bnc-skel" style="height:13px;width:70%;border-radius:4px;margin-top:4px"></div>
        <div class="bnc-skel" style="height:11px;width:40%;border-radius:4px;margin-top:4px"></div>
      </div>`).join('');
  }

  function buildSkeletonRows(n) {
    return Array.from({ length: n }, () => `
      <div class="bnc-search-skel-row" aria-hidden="true">
        <div class="bnc-skel bnc-skel-thumb"></div>
        <div class="bnc-skel-lines">
          <div class="bnc-skel bnc-skel-line-a"></div>
          <div class="bnc-skel bnc-skel-line-b"></div>
        </div>
        <div class="bnc-skel bnc-skel-price"></div>
      </div>`).join('');
  }

  /** Build result rows from suggest API response */
  function buildResultsHTML(items, query) {
    if (!items.length) {
      return `
        <div class="bnc-search-results-wrap">
          <div class="bnc-search-results-head">
            <span>Results for <b>${escHtml(query)}</b></span>
          </div>
          <div class="bnc-search-no-results-inline">
            <h4>Nothing <em>found.</em></h4>
            <p>Try a shorter word or check the spelling.</p>
          </div>
        </div>
        ${buildFooterHTML()}`;
    }

    const rows = items.map((item, i) => {
      const title    = item.title || '';
      const url      = item.url   || '#';
      const price    = item.price ? formatMoney(item.price) : '';
      const imgSrc   = item.featured_image && item.featured_image.url
        ? item.featured_image.url + '&width=100'
        : '';
      const imgAlt   = item.featured_image && item.featured_image.alt
        ? escHtml(item.featured_image.alt)
        : escHtml(title);
      const highlighted = highlightMatch(title, query);

      return `
        <a
          class="bnc-search-res-row"
          href="${escHtml(url)}"
          data-result-index="${i}"
          tabindex="-1"
        >
          <div class="bnc-search-res-img">
            ${imgSrc ? `<img src="${escHtml(imgSrc)}" alt="${imgAlt}" width="48" height="48" loading="lazy">` : ''}
          </div>
          <div class="bnc-search-res-info">
            <b>${highlighted}</b>
            <span>${escHtml(item.product_type || '')}</span>
          </div>
          <div class="bnc-search-res-price">${escHtml(price)}</div>
        </a>`;
    }).join('');

    return `
      <div class="bnc-search-results-wrap">
        <div class="bnc-search-results-head">
          <span>Results for <b>${escHtml(query)}</b></span>
          <a href="/search?q=${encodeURIComponent(query)}&type=product" style="font-family:var(--bnc-code);font-size:10.5px;color:var(--bnc-ink-2);text-decoration:none;border-bottom:1px dotted var(--bnc-line-2)">
            See all &rarr;
          </a>
        </div>
        ${rows}
      </div>
      ${buildFooterHTML()}`;
  }

  function buildFooterHTML() {
    return `
      <div class="bnc-search-foot">
        <div class="bnc-keys">
          <span><kbd>&uarr;&darr;</kbd> navigate</span>
          <span><kbd>Enter</kbd> select</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>`;
  }

  // ── Suggested queries from page data attribute ────────────────────────────

  function getSuggestedQueries() {
    const page = document.getElementById('bnc-search-page');
    if (!page) return [];
    const raw = page.dataset.suggested || '';
    return raw ? raw.split('||').filter(Boolean) : [];
  }

  // ── API fetch ─────────────────────────────────────────────────────────────

  async function fetchSuggestions(query) {
    const url = `${SUGGEST_URL}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${SUGGEST_LIMIT}`;
    const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`suggest fetch failed: ${res.status}`);
    const data = await res.json();
    return (data.resources && data.resources.results && data.resources.results.products) || [];
  }

  /** Fetch and inject preview product cards for idle state */
  async function loadPreviewCards() {
    const grid = document.getElementById('bnc-preview-grid');
    if (!grid) return;
    try {
      // Use suggested queries[0] or a generic term to pre-populate
      const queries  = getSuggestedQueries();
      const term     = queries[0] || 'tee';
      const products = await fetchSuggestions(term);
      const cards    = products.slice(0, 4).map((p) => {
        const imgSrc = p.featured_image && p.featured_image.url
          ? p.featured_image.url + '&width=300'
          : '';
        const price  = p.price ? formatMoney(p.price) : '';
        return `
          <a class="bnc-search-pcard" href="${escHtml(p.url || '#')}">
            <div class="bnc-search-pcard-img">
              ${imgSrc ? `<img src="${escHtml(imgSrc)}" alt="${escHtml(p.title || '')}" width="150" height="150" loading="lazy">` : ''}
            </div>
            <b>${escHtml(p.title || '')}</b>
            <div class="bnc-search-pcard-row">
              <span class="niche">${escHtml(p.product_type || '')}</span>
              <span class="pri">${escHtml(price)}</span>
            </div>
          </a>`;
      }).join('');
      if (cards) grid.innerHTML = cards;
    } catch (_) {
      // leave skeletons in place — non-critical
    }
  }

  // ── Panel open / close ────────────────────────────────────────────────────

  function openPanel() {
    if (!panel) return;
    panel.classList.add('open');
    panel.setAttribute('aria-hidden', 'false');
    activeIndex = -1;
    renderIdle();
  }

  function closePanel() {
    if (!panel) return;
    panel.classList.remove('open');
    panel.setAttribute('aria-hidden', 'true');
    activeIndex = -1;
  }

  function renderIdle() {
    const body = panel.querySelector('.bnc-search-panel-body');
    if (!body) return;
    body.innerHTML = buildIdleHTML();
    bindIdleEvents();
    loadPreviewCards();
  }

  function renderLoading(query) {
    const body = panel.querySelector('.bnc-search-panel-body');
    if (!body) return;
    body.innerHTML = `
      <div class="bnc-search-results-wrap">
        <div class="bnc-search-results-head">
          <span>Results for <b>${escHtml(query)}</b></span>
        </div>
        ${buildSkeletonRows(4)}
      </div>
      ${buildFooterHTML()}`;
  }

  function renderResults(items, query) {
    const body = panel.querySelector('.bnc-search-panel-body');
    if (!body) return;
    body.innerHTML = buildResultsHTML(items, query);
    activeIndex = -1;
  }

  // ── Keyboard navigation ───────────────────────────────────────────────────

  function getNavigableItems() {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll(
      '.bnc-search-res-row, .bnc-search-list a, .bnc-search-pcard'
    ));
  }

  function setActive(index) {
    const items = getNavigableItems();
    items.forEach((el, i) => {
      if (i === index) {
        el.setAttribute('aria-selected', 'true');
        el.classList.add('bnc-active');
        el.focus();
      } else {
        el.removeAttribute('aria-selected');
        el.classList.remove('bnc-active');
      }
    });
    activeIndex = index;
  }

  function handleKeydown(e) {
    if (!panel || !panel.classList.contains('open')) return;

    const items = getNavigableItems();
    if (!items.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (activeIndex <= 0) {
          activeIndex = -1;
          if (inputEl) inputEl.focus();
        } else {
          setActive(activeIndex - 1);
        }
        break;
      case 'Enter':
        if (activeIndex >= 0 && items[activeIndex]) {
          const href = items[activeIndex].getAttribute('href');
          if (href && href !== '#') {
            saveRecent(currentQuery || inputEl.value);
            window.location.href = href;
          }
        } else if (inputEl && inputEl.value.trim()) {
          submitSearch(inputEl.value.trim());
        }
        break;
      case 'Escape':
        closePanel();
        if (inputEl) inputEl.focus();
        break;
    }
  }

  function submitSearch(query) {
    saveRecent(query);
    window.location.href = `/search?q=${encodeURIComponent(query)}&type=product`;
  }

  // ── Input handler ─────────────────────────────────────────────────────────

  function onInput(e) {
    const query = e.target.value.trim();
    currentQuery = query;

    clearTimeout(debounceTimer);

    if (!query) {
      renderIdle();
      return;
    }

    renderLoading(query);

    debounceTimer = setTimeout(async () => {
      if (currentQuery !== query) return; // stale
      try {
        const items = await fetchSuggestions(query);
        if (currentQuery === query) renderResults(items, query);
      } catch (err) {
        console.error('[bnc-search] suggest error', err);
      }
    }, DEBOUNCE_MS);
  }

  // ── Idle event binding ────────────────────────────────────────────────────

  function bindIdleEvents() {
    const clearBtn = document.getElementById('bnc-clear-recent');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        clearRecent();
        renderIdle();
      });
    }
  }

  // ── Panel injection & input wiring ────────────────────────────────────────

  /**
   * Find the header search input (Dawn's predictive-search or standard search form).
   * We look for any <input type="search"> inside a header element, or fallback to
   * Dawn's predictive-search custom element.
   */
  function findHeaderSearchInput() {
    // Dawn's predictive search component
    const dawPredictive = document.querySelector('predictive-search input[type="search"]');
    if (dawPredictive) return { input: dawPredictive, wrap: dawPredictive.closest('predictive-search') || dawPredictive.parentElement };

    // Generic header search input
    const headerInput = document.querySelector('header input[type="search"], .header input[type="search"]');
    if (headerInput) return { input: headerInput, wrap: headerInput.closest('form') || headerInput.parentElement };

    // Search form in section
    const anySearch = document.querySelector('[data-bnc-search-trigger] input[type="search"]');
    if (anySearch) return { input: anySearch, wrap: anySearch.closest('[data-bnc-search-trigger]') };

    return null;
  }

  /** Inject the panel element adjacent to the search trigger wrapper. */
  function injectPanel(wrap) {
    // Avoid double injection
    if (document.getElementById('bnc-search-panel')) {
      return document.getElementById('bnc-search-panel');
    }

    const panelEl = document.createElement('div');
    panelEl.id               = 'bnc-search-panel';
    panelEl.className        = 'bnc-search-panel';
    panelEl.setAttribute('aria-hidden', 'true');
    panelEl.setAttribute('role', 'listbox');
    panelEl.innerHTML        = '<div class="bnc-search-panel-body"></div>';

    // Anchor the panel as a sibling of <header>, inside the .bnc-header-section
    // wrapper. The panel uses position:fixed (CSS) with top:var(--bnc-header-h)
    // so it stays glued to the sticky cluster's bottom on every scroll and on
    // every breakpoint, including iOS 16 Safari where position:absolute
    // children of a sticky parent can drift (memory
    // feedback_backdrop_filter_traps_fixed_descendants — the inner <header>
    // has backdrop-filter which creates a containing block trap for nested
    // fixed descendants, so the panel MUST live outside <header> for fixed
    // positioning to resolve against the viewport).
    const header = document.querySelector('sticky-header, #bnc-header, header.bnc-header');
    if (header && header.parentNode) {
      header.parentNode.appendChild(panelEl);
    } else if (header) {
      header.appendChild(panelEl);
    } else {
      wrap.style.position = 'relative';
      wrap.appendChild(panelEl);
    }

    return panelEl;
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const found = findHeaderSearchInput();
    if (!found) return; // no search input on this page

    inputEl     = found.input;
    triggerWrap = found.wrap;
    panel       = injectPanel(triggerWrap);

    // Focus opens panel
    inputEl.addEventListener('focus', () => openPanel());

    // Input triggers predictive fetch
    inputEl.addEventListener('input', onInput);

    // Click outside closes panel
    document.addEventListener('click', (e) => {
      if (!panel) return;
      if (!panel.contains(e.target) && e.target !== inputEl) {
        closePanel();
      }
    });

    // Keyboard nav
    document.addEventListener('keydown', handleKeydown);

    // Mutex: when menu panel opens, close this predictive panel.
    // Sibling listener in dopamiles-header.js closes the search overlay on
    // 'bnc:search:open'. Together: opening either surface closes the other.
    document.addEventListener('bnc:menu:open', () => {
      closePanel();
    });

    // Search page form submit: save query to recent
    const pageForm = document.querySelector('.bnc-srch-box');
    if (pageForm) {
      pageForm.addEventListener('submit', (e) => {
        const q = pageForm.querySelector('input[type="search"]');
        if (q && q.value.trim()) saveRecent(q.value.trim());
      });
    }

    // If on search page with a query, open panel won't apply — page handles results
  }

  // ── Utility ───────────────────────────────────────────────────────────────

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Wrap matching substring in <mark> for highlight */
  function highlightMatch(title, query) {
    if (!query) return escHtml(title);
    const escaped  = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex    = new RegExp(`(${escaped})`, 'gi');
    return escHtml(title).replace(
      new RegExp(`(${escHtml(query).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
      '<mark>$1</mark>'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
